import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    household_size: user?.household_size || 1,
    monthly_grocery: user?.monthly_grocery || 0,
    travel_frequency: user?.travel_frequency || "occasional",
    favorite_stores: (user?.favorite_stores || []).join(", "),
    interests: (user?.interests || []).join(", "),
    has_kids: user?.has_kids || false,
    has_pets: user?.has_pets || false,
    city: user?.city || "",
    zip_code: user?.zip_code || "",
    budget_goal: user?.budget_goal || 0,
    subscriptions_paid: user?.subscriptions_paid || 0,
    credit_cards: (user?.credit_cards || []).join(", "),
    dietary: (user?.dietary || []).join(", "),
  });

  const save = async () => {
    try {
      await api.post("/auth/onboarding", {
        ...form,
        household_size: parseInt(form.household_size),
        monthly_grocery: parseFloat(form.monthly_grocery),
        budget_goal: parseFloat(form.budget_goal),
        subscriptions_paid: parseFloat(form.subscriptions_paid),
        favorite_stores: form.favorite_stores.split(",").map((s) => s.trim()).filter(Boolean),
        interests: form.interests.split(",").map((s) => s.trim()).filter(Boolean),
        credit_cards: form.credit_cards.split(",").map((s) => s.trim()).filter(Boolean),
        dietary: form.dietary.split(",").map((s) => s.trim()).filter(Boolean),
      });
      await refreshUser();
      toast.success("Profile saved");
    } catch (e) {
      toast.error("Couldn't save");
    }
  };

  return (
    <div data-testid="page-profile">
      <PageHeader eyebrow="Profile" title="Lifestyle settings" subtitle="Update your profile to refine Ria's recommendations." />

      <Card className="border-gray-200 shadow-none rounded-2xl max-w-3xl">
        <CardContent className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="mb-1.5 block">Name</Label><Input value={user?.name || ""} disabled /></div>
            <div><Label className="mb-1.5 block">Email</Label><Input value={user?.email || ""} disabled /></div>
            <div><Label className="mb-1.5 block">Household size</Label><Input type="number" value={form.household_size} onChange={(e) => setForm({ ...form, household_size: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Monthly grocery ($)</Label><Input type="number" value={form.monthly_grocery} onChange={(e) => setForm({ ...form, monthly_grocery: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">ZIP</Label><Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Annual savings goal ($)</Label><Input type="number" value={form.budget_goal} onChange={(e) => setForm({ ...form, budget_goal: e.target.value })} /></div>
            <div><Label className="mb-1.5 block">Monthly subscriptions ($)</Label><Input type="number" value={form.subscriptions_paid} onChange={(e) => setForm({ ...form, subscriptions_paid: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Favorite stores (comma-separated)</Label><Input value={form.favorite_stores} onChange={(e) => setForm({ ...form, favorite_stores: e.target.value })} placeholder="Walmart, Target, Whole Foods" /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Interests (comma-separated)</Label><Input value={form.interests} onChange={(e) => setForm({ ...form, interests: e.target.value })} placeholder="groceries, travel, credit-cards" /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Credit cards (comma-separated)</Label><Input value={form.credit_cards} onChange={(e) => setForm({ ...form, credit_cards: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label className="mb-1.5 block">Dietary preferences (comma-separated)</Label><Input value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })} placeholder="Vegetarian, Halal" /></div>
          </div>
          <div className="flex flex-wrap gap-3 pt-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.has_kids} onChange={(e) => setForm({ ...form, has_kids: e.target.checked })} /> Has kids
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.has_pets} onChange={(e) => setForm({ ...form, has_pets: e.target.checked })} /> Has pets
            </label>
          </div>
          <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="save-profile">
            <Sparkles className="w-4 h-4 mr-2" /> Save profile
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
